import { observable, action, computed, makeObservable } from 'mobx';
import _ from 'lodash';
import apiActions from 'api/actions';
import { formatGradeLevel } from 'utils/strings';

export class Skill {
  constructor(data, root) {
    this._root = root;
    this.id = data.id;
    this.name = data.name;
    this.parent_description = data.parent_description;
    this.video_url = data.video_url;
    this.grade_level = data.grade_level;
    this.grade_id = data.grade_id;
    this.standard_id = data.standard_id;
    this.parent_share_title = data.parent_share_title;
    this.parent_share_link = data.parent_share_link;
    this.parent_share_quote = data.parent_share_quote;
    this.parent_share_tags = data.parent_share_tags;
    this.url_resources = data.url_resources;
  }

  get standard() {
    return this._root.StandardStore.find(this.standard_id);
  }

  get domain() {
    return this.standard.domain;
  }

  get grade() {
    return this.grade_id ? this._root.GradeStore.find(this.grade_id) : this.domain.grade;
  }

  get gradeLevel() {
    return this.grade_id ? this.grade.display : this.domain.gradeLevel;
  }

  get subject() {
    return this.standard ? this.domain.subject : this._root.SubjectStore.find(this.subject_id);
  }

  get journalEntries() {
    return this._root.JournalEntryStore.forSkill(this.id);
  }
}

export default class SkillStore {
  constructor(root) {
    makeObservable(this, {
      data: observable,
      requests: observable,
      add: action,
      addMany: action,
      remove: action,
      all: computed,
      reset: action,
      fetchAll: action,
      refetch: action,
      fetchAllCompleted: computed
    });

    this.root = root;
  }

  data = new Map();
  requests = new Map();

  add = data => {
    const skill = new Skill(data, this.root);
    this.data.set(skill.id, skill);
    this.root.ParentGuidanceStore.addMany(data.parent_guidances);
  }

  addMany = datas => {
    const merge = new Map();
    datas.forEach(data => {
      const skill = new Skill(data, this.root);
      merge.set(skill.id, skill);
      this.root.ParentGuidanceStore.addMany(data.parent_guidances);
    });
    this.data.merge(merge);
  }

  remove = id => {
    if (id && this.data.has(id)) this.data.delete(id);
  }

  find(id) {
    return this.data.get(id);
  }

  get all() {
    return Array.from(this.data.values());
  }

  get gradeLevels() {
    return _.chain(Array.from(this.data.values())).map('gradeLevel').uniq().sort().value();
  }

  reset = () => {
    this.data.clear();
    this.requests.clear();
  }

  fetchAll() {
    if (this.requests.get('all') === undefined) {
      this.requests.set('all', 'pending');
      return apiActions.rawGet('academics/skills').then((response) => {
        this.addMany(response.data.skills);
        this.requests.set('all', 'complete');
      });
    }
    return Promise.resolve();
  }

  refetch() {
    if (this.requests.get('all') !== undefined) {
      this.reset();
      return this.fetchAll();
    }
  }

  get fetchAllCompleted() {
    return this.requests.get('all') === 'complete';
  }
}
